import React from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import OfficeBuildingOutlineIcon from 'mdi-react/OfficeBuildingOutlineIcon';
import DoorOpenIcon from 'mdi-react/DoorOpenIcon';
import AccountMultipleOutlineIcon from 'mdi-react/AccountMultipleOutlineIcon';
import ParkingIcon from 'mdi-react/ParkingIcon';
import WrenchOutlineIcon from 'mdi-react/WrenchOutlineIcon';
import MapOutlineIcon from 'mdi-react/MapOutlineIcon';
import EyeOffOutlineIcon from 'mdi-react/EyeOffOutlineIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import * as listStyles from '../../../core/admin-list.styles';
import StyleConstants from '../../../core/styles.constants';

/**
 * `row` is the flush, full-width list row; `grid` is the bordered card used by the grid view,
 * where the count chips sit under the name. Same two shapes as the facilities and parkings lists.
 */
export type OfficeCardVariant = 'row' | 'grid';

interface Props {
  readonly id: string;
  readonly name: string;
  readonly amountOfRooms: number;
  readonly amountOfEmails: number;
  readonly isAvailableInApp: boolean;
  readonly floorPlanEnabled: boolean;
  readonly linkedParkings: number;
  readonly linkedFacilities: number;
  readonly variant?: OfficeCardVariant;
  readonly onClick?: () => void;
}

const OfficeCard = ({
  id,
  name,
  amountOfRooms,
  amountOfEmails,
  isAvailableInApp,
  floorPlanEnabled,
  linkedParkings,
  linkedFacilities,
  variant = 'row',
  onClick,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();

  // Each chip label is one translated string with the count filled in, so casing and word order
  // stay a translator's decision rather than something the component patches at runtime.
  const countLabel = (count: number, singularKey: string, pluralKey: string): string =>
    (t(count === 1 ? singularKey : pluralKey) ?? '').replace('[x]', count.toString());

  // Opening the row has to work from the keyboard too, so it behaves like the button it is.
  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.();
    }
  };

  const renderChip = (
    key: string,
    icon: React.JSX.Element,
    label: string,
    chipVariant: listStyles.ChipVariant = 'solid',
  ): React.JSX.Element => (
    <span className={style(listStyles.chip(chipVariant))} key={key} title={label}>
      <span className={style(listStyles.chipIcon)}>{icon}</span>
      {label}
    </span>
  );

  const chips = (
    <div className={style(listStyles.chipRow)}>
      {renderChip('rooms', <DoorOpenIcon size={13} />, countLabel(amountOfRooms, 'office-chip-room', 'office-chip-rooms'))}
      {renderChip(
        'members',
        <AccountMultipleOutlineIcon size={13} />,
        countLabel(amountOfEmails, 'office-chip-member', 'office-chip-members'),
      )}
      {renderChip(
        'parkings',
        <ParkingIcon size={13} />,
        countLabel(linkedParkings, 'office-chip-parking', 'office-chip-parkings'),
      )}
      {renderChip(
        'facilities',
        <WrenchOutlineIcon size={13} />,
        countLabel(linkedFacilities, 'office-chip-facility', 'office-chip-facilities'),
      )}
      {floorPlanEnabled && renderChip('floor-plan', <MapOutlineIcon size={13} />, t('floor-plan') ?? '', 'muted')}
      {/* Only the exceptional state is called out: a visible office needs no badge. */}
      {!isAvailableInApp && renderChip('hidden', <EyeOffOutlineIcon size={13} />, t('hidden') ?? '', 'warning')}
    </div>
  );

  const tile = (
    <div className={style(listStyles.rowTile(StyleConstants.DARK_BLUE_COLOR))}>
      <OfficeBuildingOutlineIcon size={20} />
    </div>
  );

  const chevron = (
    <span className={style(listStyles.rowGo)}>
      <ChevronRightIcon size={20} />
    </span>
  );

  if (variant === 'grid') {
    return (
      <div
        className={style(listStyles.gridCard)}
        key={id}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleKeyDown}
      >
        <div className={style(listStyles.gridCardTop)}>
          {tile}
          <div className={style(listStyles.rowWho)}>
            <span className={style(listStyles.rowName)} title={name}>
              {name}
            </span>
          </div>
          {chevron}
        </div>
        {chips}
      </div>
    );
  }

  return (
    <div
      className={style(listStyles.flushRow)}
      key={id}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      {tile}
      <div className={style(listStyles.rowWho)}>
        <span className={style(listStyles.rowName)} title={name}>
          {name}
        </span>
        {chips}
      </div>
      {chevron}
    </div>
  );
};

export default OfficeCard;
